import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';

interface LivisPillBarChartProps {
    data: Array<{ name: string; value: number }>;
    height?: number;
    colors?: [string, string];
    threshold?: number | null;
    noDataMessage?: string;
}

const LivisPillBarChart: React.FC<LivisPillBarChartProps> = ({
    data,
    height = 300,
    colors = [
        'var(--livis-operator-panel-mini-feed-border)',
        'var(--livis-modal-freqency-indicator-low)',
    ],
    threshold = null,
    noDataMessage = 'No Data to Display',
}) => {
    const { t } = useTranslation();
    const hasData = data && data.length > 0;

    if (!hasData) {
        return (
            <div
                style={{
                    width: '100%',
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <p style={{ color: 'var(--livis-color-primary-gray)' }}>
                    {t(noDataMessage)}
                </p>
            </div>
        );
    }

    const categories = data.map((d) => d.name);
    const values = data.map((d) => d.value);

    // Alternate colors - first color for even indices, second color for odd
    const barColors = values.map((_, index: number) =>
        index % 2 === 0 ? colors[0] : colors[1]
    );

    const options: Highcharts.Options = {
        chart: {
            type: 'column',
            backgroundColor: 'transparent',
            height: height,
        },
        title: { text: undefined },
        xAxis: {
            categories: categories,
            labels: {
                style: { fontSize: '11px', color: 'var(--livis-color-primary-gray)' },
            },
            lineWidth: 0,
            tickWidth: 0,
        },
        yAxis: {
            title: { text: undefined },
            gridLineColor: 'var(--livis-light-gray-bg-color)',
            gridLineDashStyle: 'Dash',
            labels: {
                style: { color: 'var(--livis-gray100-font-color)', fontSize: '10px' },
            },
            plotLines:
                threshold !== null && threshold !== undefined
                    ? [
                        {
                            color: '#FF9800',
                            dashStyle: 'ShortDash',
                            value: threshold,
                            width: 2,
                            zIndex: 5,
                            label: {
                                text: `${t('Target')}: ${threshold}`,
                                align: 'right',
                                x: -10,
                                style: { color: '#FF9800', fontWeight: '600' },
                            },
                        },
                    ]
                    : undefined,
        },
        legend: { enabled: false },
        plotOptions: {
            column: {
                borderRadius: 4,
                pointPadding: 0.2,
                groupPadding: 0.1,
                pointWidth: 50,
                borderWidth: 0,
                dataLabels: {
                    enabled: true,
                    inside: false,
                    verticalAlign: 'bottom',
                    align: 'center',
                    style: {
                        fontSize: '12px',
                        fontWeight: '600',
                        textOutline: 'none',
                        color: 'var(--livis-gray100-font-color)',
                    },
                },
                colorByPoint: true,
            },
        },
        colors: barColors,
        series: [
            {
                type: 'column',
                name: 'Values',
                data: values,
            },
        ],
        credits: { enabled: false },
        tooltip: {
            shared: false,
            formatter: function (this: any) {
                return `<b>${this.x}</b>: ${this.y}`;
            },
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default LivisPillBarChart;